import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { compressImage, compressFromCanvas } from "../utils/imageCompression";
import { checkImageQuality } from "../utils/imageQuality";
import { useCompressionSettings } from "../utils/CompressionContext";
import { ImageCropper } from "../components/ImageCropper";
import { VerificationHeader } from "../components/verification/VerificationHeader";
import { StepsList } from "../components/verification/StepsList";
import { StepVerificationPanel } from "../components/verification/StepVerificationPanel";

// Merge incoming session data with existing state, preserving captured_image_base64
// that the backend strips from responses to keep payloads small.
const mergeTestImages = (newData, prevTest) => {
  if (!newData?.step_results || !prevTest?.step_results) return newData;
  return {
    ...newData,
    step_results: newData.step_results.map(sr => {
      const prev = prevTest.step_results.find(e => e.step_id === sr.step_id);
      return { ...sr, captured_image_base64: sr.captured_image_base64 ?? prev?.captured_image_base64 };
    }),
  };
};

export default function VerificationFlow() {
  const { testId } = useParams();
  const { token } = useAuth();
  const compressionSettings = useCompressionSettings();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const pollIntervalsRef = useRef(new Map());   // stepId → intervalId
  const pollTimeoutsRef  = useRef(new Map());   // stepId → timeoutId
  const canvasRef = useRef(null);

  const [test, setTest] = useState(null);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzingSteps, setAnalyzingSteps] = useState(new Set());
  const [selectedImage, setSelectedImage] = useState(null);
  const [notes, setNotes] = useState("");
  const [activeStep, setActiveStep] = useState(0);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [imageQuality, setImageQuality] = useState(null);
  const [showCropper, setShowCropper] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const startAnalyzing = (stepId) =>
    setAnalyzingSteps(prev => new Set([...prev, stepId]));

  const stopAnalyzing = (stepId) =>
    setAnalyzingSteps(prev => { const n = new Set(prev); n.delete(stepId); return n; });

  const clearPoll = (stepId) => {
    if (pollIntervalsRef.current.has(stepId)) {
      clearInterval(pollIntervalsRef.current.get(stepId));
      pollIntervalsRef.current.delete(stepId);
    }
    if (pollTimeoutsRef.current.has(stepId)) {
      clearTimeout(pollTimeoutsRef.current.get(stepId));
      pollTimeoutsRef.current.delete(stepId);
    }
  };

  useEffect(() => {
    const loadTestData = async () => {
      try {
        const testRes = await axios.get(`${API}/tests/${testId}`, { headers });
        setTest(testRes.data);

        const productSteps = await axios.get(`${API}/steps?product_id=${testRes.data.product_id}`, { headers });
        setSteps(productSteps.data);

        const currentStepIndex = testRes.data.step_results.findIndex(
          sr => sr.status === "pending"
        );
        setActiveStep(currentStepIndex >= 0 ? currentStepIndex : testRes.data.step_results.length - 1);
      } catch (error) {
        toast.error("Failed to load test data");
      } finally {
        setLoading(false);
      }
    };
    loadTestData();
  }, [testId, headers]);

  useEffect(() => {
    const currentVideo = videoRef.current;
    return () => {
      if (currentVideo && currentVideo.srcObject) {
        currentVideo.srcObject.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Clear selected image and stop camera whenever the active step changes
  useEffect(() => {
    setSelectedImage(null);
    setImageQuality(null);
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(track => track.stop());
      cameraStreamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraActive(false);
  }, [activeStep]);

  useEffect(() => {
    const intervals = pollIntervalsRef.current;
    const timeouts  = pollTimeoutsRef.current;
    return () => {
      intervals.forEach(id => clearInterval(id));
      timeouts.forEach(id => clearTimeout(id));
    };
  }, []);

  // Attach stream once <video> element is rendered after setCameraActive(true)
  useEffect(() => {
    if (cameraActive && videoRef.current && cameraStreamRef.current) {
      videoRef.current.srcObject = cameraStreamRef.current;
    }
  }, [cameraActive]);

  const startCamera = async () => {
    try {
      setCameraError(null);
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const msg = window.location.protocol === "http:" && window.location.hostname !== "localhost"
          ? "Camera requires HTTPS. Upload an image instead."
          : "Camera not available in this browser.";
        setCameraError(msg);
        toast.error(msg);
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      cameraStreamRef.current = stream;
      setCameraActive(true);
    } catch (error) {
      console.error("Camera error:", error);
      setCameraError("Could not access camera. Please check permissions.");
      toast.error("Camera access denied or not available");
    }
  };

  const stopCamera = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach(track => track.stop());
      cameraStreamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraActive(false);
  };

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    if (video.videoWidth > video.videoHeight) {
      toast.warning("Landscape camera — portrait mode gives best results", { duration: 4000 });
    }
    const compressedBase64 = compressFromCanvas(video, {
      maxWidth: compressionSettings.maxWidth,
      maxHeight: compressionSettings.maxHeight,
      quality: compressionSettings.quality
    });
    setSelectedImage(compressedBase64);
    setImageQuality(null);
    stopCamera();
    checkImageQuality(compressedBase64).then((q) => {
      setImageQuality(q);
      if (q.quality === "poor") {
        toast.warning(`Image quality issue: ${q.suggestions}`, { duration: 6000 });
      } else if (q.quality === "fair") {
        toast.info(`Tip: ${q.suggestions}`, { duration: 4000 });
      }
    });
    toast.success("Photo captured!");
  }, [compressionSettings]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image file"); return; }
    try {
      // Check orientation before compression so we can warn the user
      const isLandscape = await new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img.naturalWidth > img.naturalHeight); };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(false); };
        img.src = url;
      });
      if (isLandscape) {
        toast.warning("Landscape image detected — portrait mode gives best results", { id: "compress", duration: 4000 });
      }
      toast.loading("Compressing image...", { id: "compress" });
      const compressedBase64 = await compressImage(file, {
        maxWidth: compressionSettings.maxWidth,
        maxHeight: compressionSettings.maxHeight,
        quality: compressionSettings.quality,
        maxSizeKB: compressionSettings.maxSizeKB
      });
      setSelectedImage(compressedBase64);
      setImageQuality(null);
      checkImageQuality(compressedBase64).then((q) => {
        setImageQuality(q);
        if (q.quality === "poor") {
          toast.warning(`Image quality issue: ${q.suggestions}`, { id: "compress", duration: 6000 });
        } else if (q.quality === "fair") {
          toast.info(`Tip: ${q.suggestions}`, { id: "compress", duration: 4000 });
        } else {
          toast.success("Image ready!", { id: "compress" });
        }
      });
    } catch (error) {
      toast.error("Failed to process image", { id: "compress" });
      console.error("Image compression error:", error);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleVerifyStep = async () => {
    if (!selectedImage) { toast.error("Please capture or upload an image"); return; }
    const currentStepResult = test.step_results[activeStep];
    if (!currentStepResult) return;

    const stepId = currentStepResult.step_id;
    startAnalyzing(stepId);
    try {
      const response = await axios.post(
        `${API}/tests/${testId}/verify-step`,
        { step_id: stepId, captured_image_base64: selectedImage, notes: notes },
        { headers }
      );

      setTest(prev => mergeTestImages(response.data, prev));
      setSelectedImage(null);
      setNotes("");
      setImageQuality(null);

      if (activeStep < response.data.step_results.length - 1) {
        setActiveStep(activeStep + 1);
        toast.success("Image submitted — analyzing in background", { id: "step-submit", duration: 3000 });
      } else {
        toast.info("Image submitted — analyzing last step…", { id: "step-submit", duration: 3000 });
      }

      let errorCount = 0;
      clearPoll(stepId);

      pollIntervalsRef.current.set(stepId, setInterval(async () => {
        try {
          const pollRes = await axios.get(`${API}/tests/${testId}/step-status/${stepId}`, { headers });
          errorCount = 0;
          if (pollRes.data.status !== "analyzing") {
            clearPoll(stepId);
            stopAnalyzing(stepId);
            const updatedTest = pollRes.data.test;
            if (updatedTest) setTest(prev => mergeTestImages(updatedTest, prev));
            const testDone = updatedTest && updatedTest.status !== "in_progress";
            if (testDone) {
              // Test is complete — show one final toast, suppress per-step noise.
              if (updatedTest.status === "passed") toast.success("Test passed!", { id: "test-status" });
              else toast.error("Test failed — review results", { id: "test-status", duration: 6000 });
            } else {
              // Test still in progress — show per-step result, replacing any previous one.
              const stepName = updatedTest?.step_results?.find(s => s.step_id === stepId)?.step_name || "Step";
              if (pollRes.data.status === "pass") toast.success(`${stepName}: Passed`, { id: "step-result", duration: 3000 });
              else if (pollRes.data.status === "fail") toast.error(`${stepName}: Failed — check results`, { id: "step-result", duration: 5000 });
              else if (pollRes.data.ai_analysis?.error_type) toast.error(`${stepName}: AI error — see details`, { id: "step-result", duration: 6000 });
              else toast.warning(`${stepName}: Review needed`, { id: "step-result", duration: 4000 });
            }
          }
        } catch {
          errorCount++;
          if (errorCount >= 5) {
            clearPoll(stepId);
            stopAnalyzing(stepId);
            toast.error("Lost connection while waiting for AI analysis", { id: "step-result" });
          }
        }
      }, 2000));

      pollTimeoutsRef.current.set(stepId, setTimeout(() => {
        clearPoll(stepId);
        stopAnalyzing(stepId);
        toast.warning("AI analysis is taking longer than expected. Check back shortly.");
      }, 120000));

    } catch (error) {
      toast.error("Failed to submit image");
      stopAnalyzing(stepId);
    }
  };

  const handleManualOverride = async (action, comments) => {
    const currentStepResult = test.step_results[activeStep];
    try {
      const response = await axios.post(
        `${API}/tests/${testId}/manual-override`,
        { step_id: currentStepResult.step_id, action, comments },
        { headers }
      );
      setTest(prev => mergeTestImages(response.data, prev));
      toast.success(`Step manually ${action}ed`);
    } catch (error) {
      toast.error("Failed to apply manual override");
    }
  };

  const handleSkipStep = async (reason) => {
    const currentStepResult = test.step_results[activeStep];
    try {
      const response = await axios.post(
        `${API}/tests/${testId}/skip-step`,
        { step_id: currentStepResult.step_id, reason },
        { headers }
      );
      setTest(prev => mergeTestImages(response.data, prev));
      toast.success("Step skipped");
      if (activeStep < test.step_results.length - 1) {
        setActiveStep(activeStep + 1);
      }
    } catch (error) {
      toast.error("Failed to skip step");
    }
  };

  const handleReAnalyze = async (forceGemini = false) => {
    const currentStepResult = test.step_results[activeStep];
    if (!currentStepResult) return;

    const stepId = currentStepResult.step_id;
    startAnalyzing(stepId);
    try {
      const response = await axios.post(
        `${API}/tests/${testId}/re-analyze`,
        { step_id: stepId, force_gemini: forceGemini },
        { headers }
      );
      setTest(prev => mergeTestImages(response.data, prev));
      toast.info(forceGemini ? "Re-analyzing with Gemini AI..." : "Re-analyzing with updated AI settings...");

      let errorCount = 0;
      clearPoll(stepId);

      pollIntervalsRef.current.set(stepId, setInterval(async () => {
        try {
          const pollRes = await axios.get(`${API}/tests/${testId}/step-status/${stepId}`, { headers });
          errorCount = 0;
          if (pollRes.data.status !== "analyzing") {
            clearPoll(stepId);
            stopAnalyzing(stepId);
            const updatedTest = pollRes.data.test;
            if (updatedTest) setTest(prev => mergeTestImages(updatedTest, prev));
            const stepName = updatedTest?.step_results?.find(s => s.step_id === stepId)?.step_name || "Step";
            if (pollRes.data.status === "pass") toast.success(`${stepName}: Passed (re-analysis)`);
            else if (pollRes.data.status === "fail") toast.error(`${stepName}: Failed (re-analysis)`);
            else if (pollRes.data.ai_analysis?.error_type) toast.error(`${stepName}: AI error — see details below`, { duration: 6000 });
            else toast.info(`${stepName}: Warning (re-analysis)`);
          }
        } catch {
          errorCount++;
          if (errorCount >= 5) {
            clearPoll(stepId);
            stopAnalyzing(stepId);
            toast.error("Lost connection during re-analysis");
          }
        }
      }, 2000));

      pollTimeoutsRef.current.set(stepId, setTimeout(() => {
        clearPoll(stepId);
        stopAnalyzing(stepId);
        toast.warning("Re-analysis taking longer than expected");
      }, 120000));

    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to re-analyze");
      stopAnalyzing(stepId);
    }
  };

  const handleCompleteTest = async () => {
    try {
      const response = await axios.post(`${API}/tests/${testId}/complete`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setTest(prev => mergeTestImages(response.data, prev));
      toast.success(`Test ${response.data.status}!`);
    } catch (error) {
      toast.error("Failed to complete test");
    }
  };

  const handleDownloadPdf = async (includeImages = true) => {
    setDownloadingPdf(includeImages ? 'full' : 'summary');
    try {
      const response = await axios.get(`${API}/tests/${testId}/report/pdf?include_images=${includeImages}`, {
        headers,
        responseType: 'blob'
      });

      const suffix = includeImages ? '_full' : '_summary';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `verification_report_${test.serial_number || testId}${suffix}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("PDF report downloaded!");
    } catch (error) {
      toast.error("Failed to download PDF report");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const getPhaseLabel = (phase, phaseName) => {
    if (phaseName) return phaseName;
    switch (phase) {
      case "phase_1": return "Configuration & Physical Setup";
      case "phase_2": return "Precision Wiring";
      case "phase_3": return "Functional, Display & Data Sync";
      default: return phase;
    }
  };

  const getPhaseColor = (phase, phaseColor) => {
    if (phaseColor) return phaseColor;
    switch (phase) {
      case "phase_1": return "#3b82f6";
      case "phase_2": return "#f59e0b";
      case "phase_3": return "#10b981";
      default: return "#64748b";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!test) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Test session not found</p>
        <button className="mt-4 underline" onClick={() => navigate(-1)}>Go Back</button>
      </div>
    );
  }

  const currentStepResult = test.step_results[activeStep] ?? null;
  const currentStep = steps.find(s => s.id === currentStepResult?.step_id) ?? null;
  const completedCount = test.step_results.filter(sr => ["pass", "fail", "warning", "skipped"].includes(sr.status)).length;
  const passedCount = test.step_results.filter(sr => sr.status === "pass" || sr.status === "skipped").length;
  const progress = test.total_steps > 0 ? (completedCount / test.total_steps) * 100 : 0;

  if (test.step_results.length === 0) {
    return (
      <div className="space-y-6">
        <VerificationHeader
          test={test}
          completedCount={0}
          downloadingPdf={downloadingPdf}
          onBack={() => navigate(-1)}
          onDownloadPdf={handleDownloadPdf}
          onCompleteTest={handleCompleteTest}
        />
        <div className="text-center py-16 text-muted-foreground">
          <p className="text-lg font-medium">No verification steps configured</p>
          <p className="text-sm mt-1">Add steps to this product before starting a test session.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="verification-flow">
      <canvas ref={canvasRef} className="hidden" />

      <VerificationHeader
        test={test}
        completedCount={completedCount}
        downloadingPdf={downloadingPdf}
        onBack={() => navigate(-1)}
        onDownloadPdf={handleDownloadPdf}
        onCompleteTest={handleCompleteTest}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Verification Progress</span>
            <span className="text-sm font-mono text-muted-foreground">{completedCount}/{test.total_steps} steps</span>
          </div>
          <Progress value={progress} className="h-2" />
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span className="text-emerald-600">{passedCount} passed</span>
            <span className="text-red-600">{completedCount - passedCount} failed/warning</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-3">
        <StepsList
          stepResults={test.step_results}
          steps={steps}
          activeStep={activeStep}
          onSelectStep={setActiveStep}
        />

        <StepVerificationPanel
          test={test}
          currentStepResult={currentStepResult}
          currentStep={currentStep}
          activeStep={activeStep}
          selectedImage={selectedImage}
          setSelectedImage={(img) => { setSelectedImage(img); if (!img) setImageQuality(null); }}
          imageQuality={imageQuality}
          notes={notes}
          setNotes={setNotes}
          verifying={analyzingSteps.has(test?.step_results?.[activeStep]?.step_id)}
          cameraActive={cameraActive}
          cameraError={cameraError}
          videoRef={videoRef}
          fileInputRef={fileInputRef}
          getPhaseLabel={getPhaseLabel}
          getPhaseColor={getPhaseColor}
          onStartCamera={startCamera}
          onStopCamera={stopCamera}
          onCapturePhoto={capturePhoto}
          onImageUpload={handleImageUpload}
          onVerifyStep={handleVerifyStep}
          onManualOverride={handleManualOverride}
          onSkipStep={handleSkipStep}
          onCropImage={() => setShowCropper(true)}
          onReAnalyze={handleReAnalyze}
          onPrevStep={() => setActiveStep(Math.max(0, activeStep - 1))}
          onNextStep={() => setActiveStep(Math.min(test.step_results.length - 1, activeStep + 1))}
          totalSteps={test.total_steps}
          totalStepResults={test.step_results.length}
        />

        <ImageCropper
          imageBase64={selectedImage}
          open={showCropper && !!selectedImage}
          onCrop={(cropped) => {
            setSelectedImage(cropped);
            setShowCropper(false);
            setImageQuality(null);
            checkImageQuality(cropped).then(setImageQuality);
            toast.success("Image cropped!");
          }}
          onCancel={() => setShowCropper(false)}
        />
      </div>
    </div>
  );
}
